/*
 * Changes:
 * - Added 'use client' directive for Next.js 13+ compatibility
 * - File contains client-side features: React hooks, setInterval, and moment.js date handling
 * - Enhanced to work with improved upfront token validation
 */

'use client'

import {useEffect, useRef} from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'

const OAuthCredentialsExpiryProvider = ({children, oauthCredentials, handleOauthCredentialsExpiry = null}) => {
  const oauthCredentialsExpiryIntervalIdRef = useRef(null)

  useEffect(() => {
    function resetInterval() {
      clearInterval(oauthCredentialsExpiryIntervalIdRef.current)
      oauthCredentialsExpiryIntervalIdRef.current = null
    }

    const isAuthenticated = !!oauthCredentials.accessToken

    if (isAuthenticated && !oauthCredentialsExpiryIntervalIdRef.current) {
      // Check if token is already expired before setting up interval
      if (oauthCredentials.expiresAt) {
        const tokenExpiresAt = moment.utc(oauthCredentials.expiresAt)
        const now = moment.utc()
        const isAlreadyExpired = tokenExpiresAt.isBefore(now)

        if (isAlreadyExpired) {
          if (handleOauthCredentialsExpiry) {
            handleOauthCredentialsExpiry()
          }
          return
        }
      }

      oauthCredentialsExpiryIntervalIdRef.current = setInterval(() => {
        const tokenExpiresAt = moment.utc(oauthCredentials.expiresAt)
        const now = moment.utc()
        const millisecondsUntilExpiry = tokenExpiresAt.diff(now)

        if (millisecondsUntilExpiry <= 0) {
          if (handleOauthCredentialsExpiry) {
            handleOauthCredentialsExpiry()
          } else {
            console.warn('OAuthCredentialsExpiryProvider: no expiry handler configured!')
          }
        } else {
          // Token is still valid, no action needed
        }
      }, 5000)

      // Calculate time until token expiration
      const tokenExpiresAt = moment.utc(oauthCredentials.expiresAt)
      const now = moment.utc()
      const expiresIn = tokenExpiresAt.diff(now)
    } else if (!isAuthenticated && !!oauthCredentialsExpiryIntervalIdRef.current) {
      resetInterval()
    } else if (isAuthenticated && !!oauthCredentialsExpiryIntervalIdRef.current) {
      resetInterval()
      // The useEffect will run again and create a new interval with updated credentials
    }

    return () => {
      resetInterval()
    }
  }, [oauthCredentials.accessToken, oauthCredentials.expiresAt, handleOauthCredentialsExpiry])

  return children
}

OAuthCredentialsExpiryProvider.propTypes = {
  oauthCredentials: PropTypes.shape({
    accessToken: PropTypes.string,
    refreshToken: PropTypes.string,
    expiresAt: PropTypes.string
  }).isRequired,
  handleOauthCredentialsExpiry: PropTypes.func
}

export {OAuthCredentialsExpiryProvider}
